import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { tv } from "tailwind-variants";
import { motion } from "framer-motion";
import type { ComboItem } from "../types/combo";

// Animation variants for better reusability
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export type ComboCardVariant = "basic" | "classic" | "essential" | "prime";

export interface ComboCardProps {
  combo: ComboItem;
  variant?: ComboCardVariant;
  onAddToCart?: (combo: ComboItem) => void;
  onComboClick?: (combo: ComboItem) => void;
  className?: string;
  currency?: string;
  locale?: string;
}

/* -------------------- Utils -------------------- */
const formatPrice = (value: number, currency = "USD", locale = "en-US") =>
  new Intl.NumberFormat(locale, { style: "currency", currency }).format(value);

/* -------------------- Card Classes Function -------------------- */
const getCardClasses = (variant: ComboCardVariant) => {
  const baseClasses = "rounded-2xl transition cursor-pointer overflow-hidden flex flex-col w-full h-full group focus:outline-none focus:ring-2 focus:ring-orange-400";
  
  switch (variant) {
    case "basic":
      return `${baseClasses} border-4 border-red-500 bg-red-50 shadow-sm`;
    case "classic":
      return `${baseClasses} border-4 border-blue-500 bg-blue-50 shadow-md`;
    case "essential":
      return `${baseClasses} border-4 border-green-500 bg-green-50 shadow-sm`;
    case "prime":
      return `${baseClasses} border-4 border-yellow-500 bg-yellow-50 shadow-xl`;
    default:
      return `${baseClasses} border border-gray-200 bg-white shadow-sm`;
  }
};

const imageContainer = tv({
  base: "relative aspect-[4/3] w-full flex items-center justify-center overflow-hidden",
});

const contentContainer = tv({
  base: "flex-1 flex flex-col justify-between p-3 sm:p-4 md:p-6",
});

/* -------------------- Combo Card -------------------- */
const ComboCard: React.FC<ComboCardProps> = React.memo(
  ({
    combo,
    variant = "basic",
    onAddToCart,
    onComboClick,
    className = "",
    currency = "USD",
    locale = "en-US",
  }) => {
    const navigate = useNavigate();

    // Early return if combo is null/undefined
    if (!combo) {
      return null;
    }

    // Parse price safely with null/undefined handling
    const parsedPrice = combo.combo_price != null
      ? (typeof combo.combo_price === "string"
          ? parseFloat(combo.combo_price.replace(/[^0-9.-]+/g, ""))
          : combo.combo_price)
      : 0;
    const comboPrice = isNaN(parsedPrice) ? 0 : parsedPrice;
    const offerPercentage = combo.offer_percentage || 0;

    const handleAddToCart = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (combo && onAddToCart) {
          onAddToCart(combo);
        }
      },
      [onAddToCart, combo]
    );

    const handleComboClick = useCallback(() => {
      if (combo && combo.combo_id) {
        navigate(`/combo/${combo.combo_id}`);
        if (onComboClick) {
          onComboClick(combo);
        }
      }
    }, [navigate, onComboClick, combo]);

    const handleKeyPress = useCallback(
      (e: React.KeyboardEvent) => {
        if ((e.key === "Enter" || e.key === " ") && combo && onComboClick) {
          e.preventDefault();
          onComboClick(combo);
        }
      },
      [onComboClick, combo]
    );

    return (
      <motion.div
        className={clsx(getCardClasses(variant), className)}
        onClick={handleComboClick}
        onKeyDown={handleKeyPress}
        role="button"
        tabIndex={0}
        aria-label={`View details for ${combo.title || 'combo'} combo`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{
          y: -6,
          boxShadow:
            "0 12px 20px rgba(0,0,0,0.08), 0 6px 10px rgba(0,0,0,0.05)",
        }}
        whileTap={{ scale: 0.98 }}
      >
        {/* -------- Image Section -------- */}
        <div className={imageContainer()}>
          {combo.combo_image ? (
            <img
              src={combo.combo_image}
              alt={`${combo.title || 'Combo'} combo`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              onError={(e) => {
                (e.target as HTMLImageElement).src = `https://via.placeholder.com/400x300/f8f9fa/6c757d?text=${encodeURIComponent(
                  combo.title || 'Combo'
                )}`;
              }}
            />
          ) : (
            <div className="flex items-center justify-center w-full h-full">
              <span className="text-4xl sm:text-5xl font-bold uppercase text-red-600">
                {combo.title?.charAt(0) || 'C'}
              </span>
            </div>
          )}

          {offerPercentage > 0 && (
            <motion.span
              className="absolute top-2 left-2 sm:top-3 sm:left-3 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-bold text-white bg-red-500 rounded-full shadow"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 0.4, type: "spring" }}
            >
              {offerPercentage}% OFF
            </motion.span>
          )}
        </div>

        {/* -------- Content Section -------- */}
        <motion.div 
          className={contentContainer()}
          variants={staggerContainer}
          initial="hidden"
          animate="show"
        >
          {/* Title */}
          <motion.h3
            className={clsx(
              "font-bold text-gray-900 mb-2 sm:mb-3 line-clamp-2",
              variant === "prime"
                ? "text-base sm:text-lg lg:text-xl"
                : variant === "classic"
                ? "text-sm sm:text-base lg:text-lg"
                : variant === "essential"
                ? "text-sm sm:text-base lg:text-lg"
                : "text-sm sm:text-base"
            )}
            variants={fadeInUp}
          >
            {combo.title || 'Untitled Combo'}
          </motion.h3>

          {/* Description */}
          {combo.description && (
            <motion.p
              className="text-gray-600 mb-3 sm:mb-4 text-xs sm:text-sm line-clamp-2 leading-relaxed"
              variants={fadeInUp}
            >
              {combo.description}
            </motion.p>
          )}

          {/* Price & CTA - Responsive Layout */}
          <motion.div
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            variants={fadeInUp}
          >
            <span
              className={clsx(
                "font-bold text-orange-600",
                variant === "prime"
                  ? "text-lg sm:text-xl lg:text-2xl"
                  : variant === "classic"
                  ? "text-base sm:text-lg lg:text-xl"
                  : variant === "essential"
                  ? "text-base sm:text-lg lg:text-xl"
                  : "text-sm sm:text-base lg:text-lg"
              )}
            >
              {formatPrice(comboPrice, currency, locale)}
            </span>

            {/* CTA */}
            <motion.button
              onClick={handleAddToCart}
              aria-label={`Add ${combo.title || 'combo'} to cart`}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-xs sm:text-sm font-medium rounded-xl shadow-sm focus:outline-none transition-colors text-white bg-[#F92C37] hover:bg-[#E01E2B]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              ADD TO CART
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }
);
ComboCard.displayName = "ComboCard";

export default ComboCard;
